/** Naming the patient of a meeting, including one this system does not hold.
 *
 *  Meetings created by the Israeli system (source="israel") have no patient
 *  row here - the patient lives in Xano, and only `external_patient_ref`
 *  identifies them. Every screen that shows a meeting has to say something
 *  anyway, so it says that: "IL 36024", which is the id somebody can look up
 *  in the Israeli system.
 *
 *  Before this, the response schema simply required a patient, and one Israeli
 *  meeting inside the window made the whole /meetings request fail validation -
 *  a 500 for the entire calendar rather than a gap in it.
 */

interface HasPatient {
  patient?: { full_name?: string | null; hm_id?: string | null } | null;
  external_patient_ref?: string | null;
}

/** The Israeli reference, written the way it is shown. */
export const israeliPatientRef = (meeting: HasPatient): string | null =>
  meeting.external_patient_ref ? `IL ${meeting.external_patient_ref}` : null;

/** Is this a meeting whose patient lives in the Israeli system? */
export const isIsraeliPatient = (meeting: HasPatient): boolean =>
  !meeting.patient && !!meeting.external_patient_ref;

/** What to show where the patient's name goes. */
export const patientLabel = (meeting: HasPatient): string =>
  meeting.patient?.full_name || israeliPatientRef(meeting) || 'Unknown patient';

/** What to show where the clinic id goes. An em dash, not a blank: the column
 *  is empty for a reason, and a blank reads as a rendering fault. */
export const patientHmId = (meeting: HasPatient): string =>
  meeting.patient?.hm_id || (meeting.external_patient_ref ? '—' : '');
